import React, { useCallback, useEffect, useState } from 'react';
import { PackageOpen, Search, X, AlertTriangle } from 'lucide-react';
import api from '../services/api';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import '../styles/PacchettiSpeciali.css';

/**
 * PacchettiSpeciali — le voci di catalogo che hanno una durata.
 *
 * PERCHE' UNA SCHERMATA A SE'
 * Sono prestazioni come tutte le altre - si prenotano, si eseguono, si
 * fatturano - con in piu' una data oltre la quale il pacchetto finisce.
 * Mescolate alle novecentottanta voci del catalogo, quella data non la
 * guarda nessuno: e' l'unica ragione per cui questa schermata esiste.
 * L'etichetta resta interna: in fattura non compare.
 *
 * L'ordine e' per scadenza, non per nome: la domanda che si fa aprendo
 * questa pagina e' «quale sta per finire», non «dov'e' quello che cerco».
 * Quelli senza termine stanno in fondo.
 */

const oggi = () => new Date(new Date().toDateString());

const giorniA = (data) => {
    if (!data) return null;
    const d = new Date(String(data).slice(0, 10));
    if (isNaN(d)) return null;
    return Math.round((d - oggi()) / 86400000);
};

const dataIta = (v) => {
    if (!v) return null;
    const d = new Date(String(v).slice(0, 10));
    return isNaN(d) ? null : d.toLocaleDateString('it-IT');
};

/* Come si dice quanto manca. Il numero di giorni da solo non si legge:
   «fra 43 giorni» richiede un conto, «scade il 31 marzo» no. Si danno
   tutti e due, e quello che conta e' il colore. */
const stato = (giorni) => {
    if (giorni === null) return { classe: 'senza', testo: 'senza termine' };
    if (giorni < 0) return { classe: 'finito', testo: `finito da ${Math.abs(giorni)} giorni` };
    if (giorni === 0) return { classe: 'oggi', testo: 'scade oggi' };
    if (giorni <= 30) return { classe: 'vicino', testo: `fra ${giorni} giorni` };
    return { classe: 'lontano', testo: `fra ${giorni} giorni` };
};

const PacchettiSpeciali = () => {
    const [voci, setVoci] = useState([]);
    const [cerca, setCerca] = useState('');
    const [caricando, setCaricando] = useState(true);
    const [errore, setErrore] = useState(null);

    const carica = useCallback(async () => {
        setCaricando(true);
        try {
            const r = await api.get('/prestazioni', { params: { pacchetti: 'solo' } });
            setVoci(r.data?.data?.prestazioni || []);
            setErrore(null);
        } catch (e) {
            setErrore('Non riesco a leggere l’elenco');
        } finally {
            setCaricando(false);
        }
    }, []);

    useEffect(() => { carica(); }, [carica]);

    const q = cerca.trim().toLowerCase();
    const mostrate = q
        ? voci.filter((v) => `${v.nome} ${v.codice || ''}`.toLowerCase().includes(q))
        : voci;

    const scaduti = voci.filter((v) => (giorniA(v.pacchetto_scadenza) ?? 1) < 0).length;
    const inScadenza = voci.filter((v) => {
        const g = giorniA(v.pacchetto_scadenza);
        return g !== null && g >= 0 && g <= 30;
    }).length;

    return (
        <div className="psp-pagina">
            <IntestazioneSezione
                titolo="Voci con scadenza"
                icona={<PackageOpen size={22} aria-hidden="true" />}
                sottotitolo={<>
                    Le voci di catalogo con una <b>data di termine</b>. Restano
                    prestazioni come le altre — si prenotano, si eseguono, si
                    fatturano — e in fattura dell&apos;etichetta non compare nulla:
                    qui stanno solo perché la scadenza sia sotto gli occhi di
                    qualcuno.
                </>}
            />

            {errore && <p className="psp-errore">{errore}</p>}

            {!caricando && voci.length > 0 && (
                <div className="psp-conteggi">
                    <div><span className="psp-n">{voci.length}</span><span>in tutto</span></div>
                    <div className={inScadenza ? 'psp-attenzione' : ''}>
                        <span className="psp-n">{inScadenza}</span><span>scadono entro 30 giorni</span>
                    </div>
                    <div className={scaduti ? 'psp-allarme' : ''}>
                        <span className="psp-n">{scaduti}</span><span>già finiti</span>
                    </div>
                </div>
            )}

            {voci.length > 6 && (
                <label className="psp-cerca" data-non-trattiene>
                    <Search size={15} aria-hidden="true" />
                    <input placeholder="Cerca per nome o codice" value={cerca}
                           onChange={(e) => setCerca(e.target.value)} />
                    {cerca && (
                        <button onClick={() => setCerca('')} aria-label="Togli la ricerca">
                            <X size={14} />
                        </button>
                    )}
                </label>
            )}

            {caricando ? <p className="psp-attesa">Carico…</p> : voci.length === 0 ? (
                <div className="psp-vuoto">
                    <PackageOpen size={30} strokeWidth={1.4} aria-hidden="true" />
                    <p><b>Nessuna voce con scadenza.</b></p>
                    <p>
                        Si crea dal catalogo delle prestazioni: nella scheda di una voce
                        c&apos;è la spunta <b>Voce con scadenza</b>, e sotto la data di
                        scadenza.
                    </p>
                </div>
            ) : mostrate.length === 0 ? (
                <p className="psp-vuoto-ricerca">Nessuna voce con questo nome.</p>
            ) : (
                <ul className="psp-elenco">
                    {mostrate.map((v) => {
                        const g = giorniA(v.pacchetto_scadenza);
                        const st = stato(g);
                        return (
                            <li key={v.id} className={`psp-riga psp-${st.classe}`}>
                                <div className="psp-nome">
                                    <b>{v.nome}</b>
                                    <span>
                                        {v.codice ? `${v.codice} · ` : ''}
                                        {v.macro_area || v.categoria || 'senza area'}
                                        {v.attivo ? '' : ' · non attiva'}
                                    </span>
                                </div>
                                <div className="psp-scadenza">
                                    {st.classe === 'finito' && (
                                        <AlertTriangle size={14} aria-hidden="true" />
                                    )}
                                    <span className="psp-quando">
                                        {dataIta(v.pacchetto_scadenza) || '—'}
                                    </span>
                                    <span className="psp-manca">{st.testo}</span>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {!caricando && voci.length > 0 && (
                <p className="psp-nota">
                    La scadenza si cambia dalla scheda della prestazione, nel catalogo.
                    Una voce scaduta resta qui e continua a funzionare come prima:
                    la data è un promemoria, non un blocco. Sparisce da questo elenco
                    solo togliendogli la spunta.
                </p>
            )}
        </div>
    );
};

export default PacchettiSpeciali;
